import { Rejection, StateService, Transition } from '@uirouter/core';

import { CimaCore, CimaToken, CimaUrl, XmStore } from 'core/services';
import { Account } from 'store/user/models';
import { UserErrorType } from 'my-account/constants';
import { HttpError } from 'core/services/http/error';
import { GetAccountSummary } from 'my-account/activity/routes';
import { AccountSummary, PlanSummary } from 'store/account-summary/models';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';

export function AuthGuard(cimaUrl: CimaUrl, cimaToken: CimaToken, state: StateService): Promise<void> {
    if (state.transition && state.transition.to().name.includes('account.u.')) {
        return Promise.resolve();
    }

    if (!cimaToken.isLoggedIn) {
        cimaUrl.redirectToLogin({ state: state.transition.to().name, params: state.transition.params() });

        return Promise.reject(Rejection.aborted('login first'));
    } else {
        return Promise.resolve();
    }
}

export function CustomerGuard(accountResponse: Account | HttpError, state: StateService, xmStore: XmStore, transition: Transition): Promise<void> {
    if (state.transition.to().name.includes('account.u.')) {
        return Promise.resolve();
    }

    if (accountResponse instanceof Account) {
        if (!accountResponse || (accountResponse && !accountResponse.isNotSubscribed) || (accountResponse.isAuthenticatedPreactivationUser)) {
            return GetAccountSummary(xmStore, transition, accountResponse).then((accountSummary: AccountSummary) => {
                if (!accountSummary?.planGroups?.length && (accountResponse?.isMember || accountResponse?.isViewer)) {
                    state.go('role-based-customer', { error_type: UserErrorType.NO_LINES });
        
                    return Promise.reject(Rejection.aborted('No lines for member or viewer'));
                } else {
                    return Promise.resolve();
                }
            });
            
        } else {
            if (accountResponse.isNotSubscribed) {
                return GetAccountSummary(xmStore, transition, accountResponse).then((accountSummary: AccountSummary) => {
                    if (accountSummary?.planGroups?.length > 0 && accountSummary?.planGroups?.some((planGroup: PlanSummary) => planGroup.lines.length > 0)) {
                        accountResponse.setPreactivationUser();

                        return Promise.resolve();
                    } else {
                        state.go('non-xfinity-customer', { error_type: UserErrorType.NOT_USER });

                        return Promise.resolve();
                    }
                });
            } else {
                state.go('non-xfinity-customer', { error_type: UserErrorType.NOT_USER });

                return Promise.reject(Rejection.aborted('non xfinity customer'));
            }
        }
    } else {
        const status: number = 403;
        if (accountResponse) {
            const error: ApiError = accountResponse.errors ? accountResponse.errors[0] : undefined;
            if (accountResponse.status === status && (error && error.code === 'NON_PRIMARY_USER')) {
                state.go('non-xfinity-customer', { error_type: UserErrorType.SECONDARY_USER });

                return Promise.reject(Rejection.aborted('non xfinity customer'));
            }
        }

        // case when api fails by any reason, still we wanna user let go in my-account
        return Promise.resolve();
    }
}

export function MemberViewerGuard(cimaCore: CimaCore, accountResponse: Account, state: StateService): Promise<void> {
    if (!cimaCore.isBusinessUser && (accountResponse?.isMember || accountResponse?.isViewer)) {
        // Redirect to authorization error page if the user is a member or viewer
        state.go('authorization-error');

        return Promise.reject(Rejection.aborted('route not allowed for member or viewer'));
    }
    
    return Promise.resolve();
}

export function LevelMoveLandingGuard(accountInfo: Account, cimaCore: CimaCore, state: StateService, transition: Transition): Promise<void> {
    const canAccessLevelMove: boolean =
        (accountInfo.canMoveLevel || (accountInfo.isSegmentFive && !cimaCore.isBusinessUser))
        && !accountInfo.isNYCustomer;

    if (!canAccessLevelMove) {
        transition.abort();
        state.go('account.settings');

        return Promise.reject(Rejection.aborted('User is not eligible for level move'));
    }

    return Promise.resolve();
}

export function AuthLogout(cimaUrl: CimaUrl): Promise<void> {
    cimaUrl.redirectToLogout();

    return Promise.reject(Rejection.aborted('state never allowed to enter'));
}

// Helper functions
export function handleMyAccount403Error(state: StateService, options?: WatchSecondaryUserRouteOptions): Promise<void> {
    if (options && options.isWatch) {
        state.go('watch.non-xfinity-user', { ...options.params, previousStateName: options.previousStateName });
    } else {
        state.go('non-xfinity-customer', { error_type: UserErrorType.SECONDARY_USER });
    }

    return Promise.resolve();
}

export function WatchesAuthGuard(cimaUrl: CimaUrl, cimaToken: CimaToken, state: StateService): Promise<void> {
    if (!cimaToken.isLoggedIn) {
        cimaUrl.redirectToLogin({ state: state.transition.to().name, params: state.transition.params() }, 'watches');

        return Promise.reject(Rejection.aborted('login first'));
    } else {
        return Promise.resolve();
    }
}

export function WatchSecondaryUserGuard(accountResponse: Account | HttpError, state: StateService, transition: Transition): Promise<void> {
    if (accountResponse instanceof Account) {
        return Promise.resolve();
    }

    const options: WatchSecondaryUserRouteOptions = {
        isWatch: true,
        previousStateName: transition.to().name,
        params: transition.params()
    };

    return handleMyAccount403Error(state, options);
}

export function EsimAuthGuard(cimaUrl: CimaUrl, cimaToken: CimaToken, state: StateService): Promise<void> {
    if (!cimaToken.isLoggedIn) {
        cimaUrl.redirectToLogin({ state: state.transition.to().name, params: state.transition.params() }, 'esim');

        return Promise.reject(Rejection.aborted('login first'));
    } else {
        return Promise.resolve();
    }
}

export function MaintenanceGuard(cimaCore: CimaCore, state: StateService, transition: Transition): Promise<void> {
    const config: IXMOptions = transition.injector().get(CONFIG_TOKEN);
    const isMaintenanceEnabled: boolean = cimaCore.isBusinessUser ? config.ENABLE_CBM_MAINTENANCE : config.ENABLE_RESI_MAINTENANCE;

    if (isMaintenanceEnabled) {
        state.go('maintenance');

        return Promise.reject(Rejection.aborted('maintenance mode enabled'));
    }

    return Promise.resolve();
}


